import { useCallback, useMemo } from "react";
import {
  Purchases,
  type Package,
  PackageType,
  PeriodUnit,
} from "@revenuecat/purchases-js";
import { useQuery } from "@tanstack/react-query";
import { useAtomValue } from "jotai";

import useAuth from "./useAuth";
import { revenueCatReadyAtom } from "../jotai/atoms/subscription";
import { QUERY_KEYS } from "../react-query/constants";
import { TTL } from "../utils/constants";
import { formatPrice } from "../utils/pricing";
import usePurchaseSubscriptionMutation from "../react-query/mutations/use-purchase-subscription";
import { FunctionWithVoidReturn } from "../utils/constants/generic-types";

const PERIOD_SUFFIX: Record<PeriodUnit, string> = {
  [PeriodUnit.Year]: "year",
  [PeriodUnit.Month]: "month",
  [PeriodUnit.Week]: "week",
  [PeriodUnit.Day]: "day",
};

const PLAN_COPY_KEYS: Partial<
  Record<
    PackageType,
    { labelKey: string; subtitleKey: string; billingKey: string }
  >
> = {
  [PackageType.Weekly]: {
    labelKey: "paywall.weekly",
    subtitleKey: "paywall.subtitle_weekly",
    billingKey: "paywall.billed_weekly",
  },
  [PackageType.Monthly]: {
    labelKey: "paywall.monthly",
    subtitleKey: "paywall.subtitle_monthly",
    billingKey: "paywall.billed_monthly",
  },
  [PackageType.Annual]: {
    labelKey: "paywall.yearly",
    subtitleKey: "paywall.subtitle_yearly",
    billingKey: "paywall.billed_yearly",
  },
};

const EMPTY_PRICING = {
  price: "—",
  perMonth: "",
  billingDisplay: "",
  originalPrice: null as string | null,
  discount: null as string | null,
};

export const getPlanCopyKeys = (packageType: PackageType | undefined) =>
  packageType ? (PLAN_COPY_KEYS[packageType] ?? null) : null;

const billingCycleMonths = (pkg: Package): number | null => {
  const unit = pkg.webBillingProduct?.period?.unit;
  const count = pkg.webBillingProduct?.period?.number;
  if (unit === PeriodUnit.Year && count) return count * 12;
  if (unit === PeriodUnit.Month && count) return count;
  return null;
};

export const usePaywallPricing = () => {
  const { isLoggedIn } = useAuth();
  const isRevenueCatReady = useAtomValue(revenueCatReadyAtom);

  const { data: offerings, isLoading: isLoadingOfferings } = useQuery({
    queryKey: [QUERY_KEYS.SUBSCRIPTION.OFFERINGS],
    queryFn: () => Purchases.getSharedInstance().getOfferings(),
    enabled: isLoggedIn && isRevenueCatReady,
    staleTime: TTL.HOURS(1),
    gcTime: TTL.DAYS(1),
  });

  const { mutateAsync: purchaseMutation, isPending: isPurchasing } =
    usePurchaseSubscriptionMutation();

  const availablePackages = useMemo(
    () => offerings?.current?.availablePackages ?? [],
    [offerings]
  );

  const monthlyPackage = useMemo(
    () =>
      availablePackages.find((p) => p.packageType === PackageType.Monthly) ??
      null,
    [availablePackages]
  );

  const yearlyPackage = useMemo(
    () =>
      availablePackages.find((p) => p.packageType === PackageType.Annual) ??
      null,
    [availablePackages]
  );

  const findPackageByProductId = useCallback(
    (productIdentifier: string | undefined) =>
      availablePackages.find(
        (p) => p.webBillingProduct?.identifier === productIdentifier
      ) ?? null,
    [availablePackages]
  );

  const calculatePricing = useCallback(
    (pkg: Package | null) => {
      if (!pkg?.webBillingProduct?.price) return EMPTY_PRICING;

      const { price, period } = pkg.webBillingProduct;
      const { formattedPrice, currency, amountMicros } = price;
      const priceInDollars = amountMicros / 1_000_000;
      const billingMonths = billingCycleMonths(pkg);
      const asCurrency = (v: number) => formatPrice(v, currency);

      const billingDisplay = period?.unit
        ? `${formattedPrice}/${PERIOD_SUFFIX[period.unit]}`
        : formattedPrice;

      const perMonth =
        billingMonths && billingMonths > 1
          ? `${asCurrency(priceInDollars / billingMonths)}/month`
          : "";

      // To figure out the "X months OFF" discount by comparing against monthly pricing
      const monthlyRate =
        (monthlyPackage?.webBillingProduct?.price?.amountMicros ?? 0) /
        1_000_000;

      let strikethroughPrice: string | null = null;
      let discount: string | null = null;

      if (billingMonths && billingMonths > 1 && monthlyRate > 0) {
        const fullMonthlyPrice = monthlyRate * billingMonths;
        if (fullMonthlyPrice > priceInDollars) {
          strikethroughPrice = asCurrency(fullMonthlyPrice);
          const monthsSaved = Math.round(
            billingMonths - priceInDollars / monthlyRate
          );
          if (monthsSaved > 0) {
            discount = `${monthsSaved} month${monthsSaved > 1 ? "s" : ""} OFF`;
          }
        }
      }

      return {
        price: formattedPrice,
        perMonth,
        billingDisplay,
        originalPrice: strikethroughPrice,
        discount,
      };
    },
    [monthlyPackage]
  );

  const purchase = useCallback(
    (pkg: Package, onSuccess?: FunctionWithVoidReturn) =>
      purchaseMutation(pkg, { onSuccess }),
    [purchaseMutation]
  );

  return {
    availablePackages,
    yearlyPackage,
    isLoading: isLoadingOfferings || isPurchasing,
    isPurchasing,
    findPackageByProductId,
    calculatePricing,
    purchase,
  };
};
